import React, { useEffect, useState, useCallback } from 'react';
import { Loader2, Plus, Bell, Image as ImageIcon, X, Zap, Snowflake, Droplet, Sparkles, Wifi, Shield, Sofa, MoreHorizontal, Clock, Wrench } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { operacionesApi } from '../../lib/api';
import { cn } from '../../lib/utils';

const CATEGORIAS = [
  { v: 'electricidad', label: 'Electricidad', icon: Zap, color: 'text-amber-600' },
  { v: 'climatizacion', label: 'Climatización', icon: Snowflake, color: 'text-cyan-600' },
  { v: 'plomeria', label: 'Plomería', icon: Droplet, color: 'text-blue-600' },
  { v: 'limpieza', label: 'Limpieza', icon: Sparkles, color: 'text-emerald-600' },
  { v: 'internet', label: 'Internet/WiFi', icon: Wifi, color: 'text-indigo-600' },
  { v: 'seguridad', label: 'Seguridad', icon: Shield, color: 'text-red-600' },
  { v: 'mobiliario', label: 'Mobiliario', icon: Sofa, color: 'text-purple-600' },
  { v: 'otros', label: 'Otros', icon: MoreHorizontal, color: 'text-slate-600' },
];

const ESTADOS = {
  recibido: { label: 'Recibido', cls: 'bg-amber-50 text-amber-800 border-amber-200' },
  en_proceso: { label: 'En proceso', cls: 'bg-blue-50 text-blue-800 border-blue-200' },
  resuelto: { label: 'Resuelto', cls: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
};

export default function EdificioIncidencias() {
  const [misInc, setMisInc] = useState([]);
  const [unidadHist, setUnidadHist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [m, u] = await Promise.all([
        operacionesApi.misIncidencias(),
        operacionesApi.miUnidadResumen(),
      ]);
      setMisInc(m.data || []);
      const unidadId = u.data?.yo?.unidad_id;
      if (unidadId) {
        const h = await operacionesApi.historialIncidenciasUnidad(unidadId);
        setUnidadHist(h.data || []);
      }
    } catch (e) {
      toast.error('Error al cargar');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  if (loading) return <div className="p-12 text-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400 inline" /></div>;

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-rose-100 flex items-center justify-center">
            <Bell className="w-5 h-5 text-rose-700" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900">Reclamos</h1>
            <p className="text-xs text-slate-500">Reportá problemas y seguí su estado.</p>
          </div>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="btn-nueva-incidencia">
          <Plus className="w-3.5 h-3.5 mr-1" /> Nueva incidencia
        </Button>
      </div>

      <Section title="Mis reportes" items={misInc} empty="No reportaste incidencias todavía." />
      <Section title="Historial de mi unidad" items={unidadHist} empty="Sin historial." showReporter />

      <DialogNueva open={open} onClose={() => setOpen(false)} onSaved={reload} />
    </div>
  );
}

const Section = ({ title, items, empty, showReporter }) => (
  <section className="bg-white border border-slate-200 rounded-lg p-3">
    <h3 className="font-semibold text-slate-900 text-sm mb-2">{title}</h3>
    {items.length === 0 ? (
      <p className="text-xs text-slate-400 py-2">{empty}</p>
    ) : (
      <div className="space-y-2">
        {items.map(i => <IncidenciaCard key={i.incidencia_id} i={i} showReporter={showReporter} />)}
      </div>
    )}
  </section>
);

const IncidenciaCard = ({ i, showReporter }) => {
  const cat = CATEGORIAS.find(c => c.v === i.categoria);
  const Icon = cat?.icon || MoreHorizontal;
  const st = ESTADOS[i.estado] || ESTADOS.recibido;
  return (
    <div className="border border-slate-200 rounded p-2.5 flex gap-3">
      <div className={cn('w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center flex-shrink-0', cat?.color)}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-slate-900 text-sm">{cat?.label || i.categoria}</span>
          <span className={cn('text-[10px] px-1.5 py-0.5 rounded border uppercase font-medium', st.cls)}>{st.label}</span>
          <span className="text-xs text-slate-400">{new Date(i.created_at).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
        </div>
        <p className="text-sm text-slate-700 mt-0.5">{i.descripcion}</p>
        {showReporter && i.reporter_nombre && (
          <p className="text-[11px] text-slate-500 mt-0.5">Reportó: {i.reporter_nombre}</p>
        )}
        {i.foto_data_url && (
          <a href={i.foto_data_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 mt-1 text-[11px] text-indigo-600 hover:underline">
            <ImageIcon className="w-3 h-3" /> Ver foto
          </a>
        )}
        {i.historial?.length > 1 && (
          <details className="mt-1.5">
            <summary className="text-[11px] text-slate-500 cursor-pointer hover:text-slate-700">Historial ({i.historial.length})</summary>
            <ul className="mt-1 space-y-0.5 pl-3">
              {i.historial.map((h, idx) => (
                <li key={idx} className="text-[11px] text-slate-600">
                  <Clock className="w-2.5 h-2.5 inline mr-1 text-slate-400" />
                  <b>{ESTADOS[h.estado]?.label || h.estado}</b> · {new Date(h.at).toLocaleString('es-AR')} · {h.by_nombre} {h.nota && `— "${h.nota}"`}
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
    </div>
  );
};

const DialogNueva = ({ open, onClose, onSaved }) => {
  const [form, setForm] = useState({ categoria: 'otros', descripcion: '' });
  const [foto, setFoto] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error('La foto excede 5MB'); return; }
    const r = new FileReader();
    r.onload = () => setFoto(r.result);
    r.readAsDataURL(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await operacionesApi.crearIncidencia({
        categoria: form.categoria,
        descripcion: form.descripcion,
        foto_data_url: foto,
      });
      toast.success('Incidencia reportada');
      setForm({ categoria: 'otros', descripcion: '' });
      setFoto(null);
      onClose();
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al reportar');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <form onSubmit={submit}>
          <DialogHeader><DialogTitle>Reportar incidencia</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Categoría</Label>
              <div className="grid grid-cols-4 gap-1.5 mt-1">
                {CATEGORIAS.map(c => {
                  const Icon = c.icon;
                  const active = form.categoria === c.v;
                  return (
                    <button key={c.v} type="button" onClick={() => setForm({ ...form, categoria: c.v })}
                      className={cn(
                        'p-2 rounded border text-[11px] flex flex-col items-center gap-1 transition',
                        active ? 'border-rose-400 bg-rose-50 text-rose-900' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                      )}
                      data-testid={`cat-${c.v}`}>
                      <Icon className={cn('w-4 h-4', active ? 'text-rose-700' : c.color)} />
                      {c.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <Label>Descripción *</Label>
              <Textarea required rows={3} placeholder="Detallá el problema (ej: el aire acondicionado no enfría desde el lunes)..."
                value={form.descripcion} onChange={e => setForm({ ...form, descripcion: e.target.value })}
                data-testid="form-descripcion" />
            </div>
            <div>
              <Label>Foto (opcional, máx 5MB)</Label>
              {foto ? (
                <div className="mt-1 relative inline-block">
                  <img src={foto} alt="preview" className="max-h-40 rounded border border-slate-200" />
                  <button type="button" onClick={() => setFoto(null)} className="absolute -top-1.5 -right-1.5 bg-white rounded-full p-0.5 shadow border border-slate-200">
                    <X className="w-3 h-3 text-slate-500" />
                  </button>
                </div>
              ) : (
                <Input type="file" accept="image/*" onChange={handleFile} className="mt-1" data-testid="form-foto" />
              )}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={saving} data-testid="form-submit-incidencia">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Reportar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
